// ** Type Imports
import { type FreePlan, type PlanKey } from '@/types'

export enum UserStatus {
	ACTIVE = 'ACTIVE',
	INACTIVE = 'INACTIVE',
	PENDING = 'PENDING',
	DELETED = 'DELETED'
}

export enum SubscriptionStatus {
	trialing = 'trialing',
	active = 'active',
	incomplete = 'incomplete',
	incomplete_expired = 'incomplete_expired',
	past_due = 'past_due',
	canceled = 'canceled',
	unpaid = 'unpaid',
	paused = 'paused'
}

export interface UserDTO {
	id: number
	email: string
	lastName: string | null
	phoneNum: string
	firstName: string | null
	isAccountActivated: boolean
	photoKey: 'string'
	role: UserRoleDTO
	organizationId: number
	stripeCustomerId: string
	activeSubscription: boolean
	// lets the organization use the app without a subscription; older
	// responses may omit it
	freePlan?: FreePlan | null
	// typo on BE
	freeTrail: boolean
	showSubscriptionModal: boolean
	organization: {
		id: number
		name: string
		isFormSubmitted: boolean
		isFormSkiped: boolean
	}

	whitelist: {
		id: number
		email: string
		expiresAt: string | null
		maxClientNum: number | null
	} | null

	adminApproved: boolean

	status: UserStatus
	subscriptionStatus: SubscriptionStatus

	subscriptionId: string
	priceId: string
	// every Stripe price on the subscription (`priceId` is the primary one)
	priceIds: string[]
	planKey: PlanKey | null

	subscriptionStartDate: string
	subscriptionEndDate: string | null

	trialStart: string | null
	trialEnd: string | null

	currentPeriodStart: string | null
	currentPeriodEnd: string | null
	lastStripeUpdate: string | null

	cancelAt: string | null
	canceledAt: string | null
	cancelAtPeriodEnd: boolean

	tawkToUserHash: string

	// ** Custom (negotiated) pricing
	// Read straight from the DB, so these are cheap. Live price details come
	// from the custom-pricing endpoints instead.
	stripeCustomPriceId: string | null
	customMaxClientCount: number | null
	customPlanNote: string | null
}

export const enum UserRoleCode {
	SUPERADMIN = 1694,
	ADMIN = 4631,
	OWNER = 2540,
	MEMBER = 3480
}

export const enum UserRoleName {
	SUPERADMIN = 'Superadmin',
	ADMIN = 'Admin',
	MEMBER = 'Member',
	OWNER = 'Owner'
}

export interface UserRoleDTO {
	code: UserRoleCode
	name: UserRoleName
}

export enum StatusName {
	ACTIVE = 'ACTIVE',
	INACTIVE = 'INACTIVE',
	PENDING = 'PENDING'
}

// ** Admin User Management Types
export interface UserListItemDTO {
	id: number
	email: string
	firstName: string | null
	lastName: string | null
	phoneNum: string
	isAccountActivated: boolean
	role: UserRoleDTO
	status: StatusName
	photoKey: string | null
	firstLogin: string | null
	lastActivity: string | null
	organizationId: number
	clientsCount: number
	membersCount: number
	adminsCount: number
	subscriptionStatus: string | null
	organizationName: string
}

export interface GetUsersModel {
	totalCount: number
	users: UserListItemDTO[]
}

export interface GetUsersVariables
	extends Record<string, string | number | string[] | undefined> {
	search: string
	size: number
	page: number
	subscriptionStatus?: string[]
}

export interface UpdateUserRoleVariables {
	userId: number
	roleCode: UserRoleCode
}

// ** Admin Dashboard Widget Types
export interface MostSavedMonthlyMembersModel {
	year: number
	monthlyMemberCounts: MonthlyMembersDTO[]
	mostSaved: MostSavedDTO[]
}

export interface MonthlyMembersDTO {
	count: number
	month: number
}

export interface MostSavedDTO {
	id: number
	name: string
	saveAmount: number
}

export interface MostSavedMonthlyMembersVariables
	extends Record<string, string | number | undefined> {
	year: number
}
